import { getCore } from '../../../client/core/getCore';
import type { DynamicClient } from '../../../client/types';
import { createApiClient } from '../../../modules/apiClient';
import { NONCE_POOL_EXPIRATION_TIME, NONCE_POOL_SIZE } from '../constants';
import { updateNoncePoolStorage } from '../noncePoolStorage/updateNoncePoolStorage/updateNoncePoolStorage';

/**
 * Fetches a batch of nonces from the API and appends them to the pool in
 * storage.
 *
 * Prefetching is critical for iOS deep link wallet providers (e.g. Phantom
 * redirect). On iOS, a network request (like fetching a nonce) between a user
 * action and a deeplink call will break the gesture chain, causing iOS to
 * silently ignore the deeplink. By prefetching nonces ahead of time,
 * `getNonce` can return from the stored pool without an interrupting network
 * round-trip.
 *
 * New nonces are **appended** to the stored pool so that unconsumed entries
 * are not discarded by a background refetch; an expired stored pool is
 * replaced instead, since its nonces have already been evicted server-side.
 *
 * Concurrent refills are coalesced behind a single in-flight promise so that
 * simultaneous callers share one `GET /nonces` round-trip instead of each
 * issuing their own (which would inflate the pool and hammer the backend).
 * @notInstrumented
 */
export const fetchAndStoreNonces = async (
  client: DynamicClient
): Promise<void> => {
  const core = getCore(client);

  // Join an already-running refill rather than starting a second request.
  const inFlight = core.state.get().nonceFetchInFlight;
  if (inFlight) {
    return inFlight;
  }

  const { environmentId } = core;

  const apiClient = createApiClient({}, client);

  const fetchPromise = (async () => {
    const { nonces } = await apiClient.getNonces({
      count: NONCE_POOL_SIZE,
      environmentId,
    });

    const expiration = Date.now() + NONCE_POOL_EXPIRATION_TIME;

    await updateNoncePoolStorage(
      {
        update: (stored) => {
          const storedNonces =
            stored && stored.expiration > Date.now() ? stored.nonces : [];

          return {
            expiration,
            nonces: [...storedNonces, ...nonces],
          };
        },
      },
      client
    );
  })().finally(() => {
    // Release the guard once settled (including on rejection) so the next
    // low-pool refill can proceed.
    core.state.set({ nonceFetchInFlight: null });
  });

  core.state.set({ nonceFetchInFlight: fetchPromise });

  return fetchPromise;
};
